import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';

import { formatQty } from '../utils/bulkLotNdcStatus';
import { isRecordableNdc } from '../utils/bulkManualLotEntry';
import { warningTextTone, warningTint, warningTone } from '../utils/warningTone';

/**
 * POM-6010 / POM-6121 — the NDC cards on the Change NDC & Lot picker, and the partner preference
 * note that belongs under them. POM-6534: two cards per row.
 *
 * Extracted from ChangeNdcLotDialog when POM-6121 gave the cards a second job. They are no longer
 * only a stock list: in manual mode a card with no usable lot is the NDC a hand-recorded claim is
 * filed against, so it has to be selectable, and it says so rather than reading as stock on hand.
 *
 * Deliberately the ONLY NDC control in the dialog. The manual panel below has no NDC field of its
 * own, because two controls for one choice are two sources of truth for it.
 */
const ChangeNdcLotNdcCards = ({
  ndcCards,
  ndcOptions,
  selectedNdc,
  preferredNdc,
  manualMode,
  facilityName,
  onSelectNdc,
}) => {
  const preferredOffered = !!preferredNdc && ndcCards.some((card) => card.ndc === preferredNdc);
  /**
   * The preference is stated whenever there is one, not only when it is being ignored.
   *
   * Saying it only on divergence meant the box appeared at the moment the user did something
   * "wrong" and vanished when they fixed it — so the preference read as a telling-off rather than
   * as a fact about the partner. Standing text lets the user confirm they are on the right NDC,
   * which is the more common thing they want to know.
   *
   * Guarded on `selectedNdc` because the seed is skipped when the product no longer offers the
   * row's NDC: with nothing picked there is no relationship to report yet.
   */
  const showPreference = !!preferredNdc && !!selectedNdc;
  const offPreference = showPreference && selectedNdc !== preferredNdc;

  return (
    <>
      <Stack direction='row' alignItems='center' sx={{ gap: 1, mb: 1 }}>
        <Typography variant='subtitle1' component='h3' sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          NDC
        </Typography>
        {/* In manual mode the cards stop being a stock list and become the question "which NDC
            does the lot in your hand belong to", so the caption asks that instead. */}
        <Typography variant='body2' sx={{ color: 'grey.800' }}>
          {manualMode ? 'Pick the NDC the lot belongs to' : `${ndcCards.length} for this product`}
        </Typography>
      </Stack>

      {/* Deliberately not A–Z: the catalogue's own order (default NDC first), then the
          inventory-master-only NDCs, exactly as buildNdcCards returns them. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 1,
        }}
      >
        {ndcCards.map((card) => {
          const usable = card.usableLots;
          const totalAvailable = usable.reduce((sum, lot) => sum + Number(lot.remainingQty), 0);
          const isSelected = card.ndc === selectedNdc;
          /* POM-6121: a card with no usable lot is disabled OUTSIDE manual mode, as it always
             was — there is nothing to dispense from it. Inside manual mode it has to be pickable,
             because it is the card the claim is recorded against. */
          const isRecordableCard = manualMode && isRecordableNdc({ ndc: card.ndc, ndcOptions });
          const isDisabled = usable.length === 0 && !isSelected && !isRecordableCard;
          const isRecordOnly = isRecordableCard && usable.length === 0;

          let stockLine = `${formatQty(totalAvailable)} available`;
          if (usable.length === 0) {
            stockLine = isRecordOnly ? 'Record against this NDC' : 'No stock';
          }

          return (
            /* A real button, as the approved mockup draws it: that is what makes the card
               reachable by Tab and operable by Enter or Space. */
            <Paper
              key={card.ndc}
              component='button'
              type='button'
              aria-pressed={isSelected}
              variant='outlined'
              disabled={isDisabled}
              onClick={() => onSelectNdc(card.ndc)}
              sx={{
                p: 1.5,
                minWidth: 0,
                borderRadius: 2,
                font: 'inherit',
                textAlign: 'left',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.5 : 1,
                /* Dashed amber for a card that holds nothing: it reads as "record here" rather
                   than as stock on hand. Not the theme's warning palette — that is the brand
                   navy and would read as a second DEF chip. See utils/warningTone. */
                borderStyle: isRecordOnly && !isSelected ? 'dashed' : 'solid',
                borderColor: (theme) => {
                  if (isSelected) return theme.palette.primary.main;

                  return isRecordOnly ? warningTint(theme, 0.6) : undefined;
                },
                borderWidth: isSelected ? 2 : 1,
                bgcolor: (theme) =>
                  isSelected ? alpha(theme.palette.primary.main, 0.06) : undefined,
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
              title={isDisabled ? 'No usable stock for this NDC at this facility' : undefined}
            >
              <Box
                sx={{ display: 'grid', gridTemplateColumns: '20px minmax(0, 1fr)', columnGap: 1 }}
              >
                {/* Visual only: the card itself is the button and carries aria-pressed. */}
                {isSelected ? (
                  <RadioButtonCheckedIcon
                    aria-hidden
                    sx={{ fontSize: '1.15rem', color: 'primary.main', mt: 0.25 }}
                  />
                ) : (
                  <RadioButtonUncheckedIcon
                    aria-hidden
                    sx={{ fontSize: '1.15rem', color: 'text.secondary', mt: 0.25 }}
                  />
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction='row' spacing={0.5} alignItems='center'>
                    <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
                      {card.ndc}
                    </Typography>
                    {card.isDefault && <Chip label='DEF' size='small' />}
                    {card.ndc === preferredNdc && (
                      <Chip
                        label='PREF'
                        size='small'
                        variant='outlined'
                        sx={{
                          fontWeight: 700,
                          color: warningTone,
                          borderColor: (theme) => warningTint(theme, 0.6),
                          bgcolor: (theme) => warningTint(theme, 0.12),
                        }}
                      />
                    )}
                  </Stack>
                  <Typography variant='caption' color='text.secondary' component='div'>
                    {card.manufacturer}
                  </Typography>
                  <Typography
                    variant='caption'
                    component='div'
                    color={usable.length === 0 ? 'text.disabled' : 'success.main'}
                    sx={isRecordOnly ? { color: warningTextTone } : undefined}
                  >
                    {stockLine}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Advisory only — Apply stays enabled. The user may have a good reason, and a partner
          preference is not a clinical constraint. Rendered under the cards rather than as a
          dialog-level banner so it reads as a consequence of the pick just made. */}
      {showPreference && (
        <Alert
          severity='warning'
          icon={
            offPreference ? (
              <WarningAmberIcon fontSize='inherit' />
            ) : (
              <CheckCircleOutlineIcon fontSize='inherit' />
            )
          }
          sx={{
            mt: 1.5,
            /* severity='warning' alone paints this in the brand navy, which made a warning look
               like a note. Tinted amber instead, matching every other warning in the app. */
            color: 'text.primary',
            bgcolor: (theme) => warningTint(theme, 0.14),
            border: '1px solid',
            borderColor: (theme) => warningTint(theme, 0.45),
            '& .MuiAlert-icon': { color: warningTone },
          }}
        >
          <Typography variant='body2'>
            {`This partner prefers ${preferredNdc} for this drug.`}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {!offPreference && 'That is what you have selected.'}
            {offPreference &&
              (preferredOffered
                ? `You are selecting ${selectedNdc}. You can still apply it.`
                : `Their preferred NDC is not stocked at ${
                    facilityName ?? 'this facility'
                  }, so ${selectedNdc} is the closest you can pick here.`)}
          </Typography>
        </Alert>
      )}
    </>
  );
};

ChangeNdcLotNdcCards.propTypes = {
  /** From buildNdcCards — the availability catalogue merged with the inventory-master NDC list. */
  ndcCards: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  /** The closed inventory-master list, which decides which cards are recordable. */
  ndcOptions: PropTypes.arrayOf(PropTypes.shape({})),
  selectedNdc: PropTypes.string,
  preferredNdc: PropTypes.string,
  manualMode: PropTypes.bool,
  facilityName: PropTypes.string,
  onSelectNdc: PropTypes.func.isRequired,
};

ChangeNdcLotNdcCards.defaultProps = {
  ndcOptions: [],
  selectedNdc: null,
  preferredNdc: null,
  manualMode: false,
  facilityName: null,
};

export default ChangeNdcLotNdcCards;
